// =====================================================
// COMPONENTE SISA - Sistema de Información Simplificado Agrícola
// src/pages/Agro/Productor/DatosSisa.js
// =====================================================
// Permite:
// 1. Consultar automáticamente el SISA desde AFIP
// 2. Cargar datos manualmente como fallback
// =====================================================

import React, { useState } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  Alert,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
  Progress,
} from "reactstrap"
import { Formik, Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"

// Esquema de validación para carga manual
const sisaValidationSchema = Yup.object().shape({
  estadoSisa: Yup.number()
    .required("El estado SISA es requerido")
    .oneOf([1, 2, 3], "Estado inválido"),
  categoriaSisa: Yup.string(),
  activoSisa: Yup.boolean(),
  observaciones: Yup.string().max(500, "Máximo 500 caracteres"),
})

// Configuración visual por estado SISA
const getEstadoConfig = estado => {
  switch (estado) {
    case 0:
      return {
        color: "secondary",
        label: "Estado 0 - Sin Actividad",
        icon: "mdi mdi-shield-off",
        descripcion: "Sin actividad registrada o dado de baja.",
      }
    case 1:
      return {
        color: "success",
        label: "Estado 1 - Bajo Riesgo",
        icon: "mdi mdi-shield-check",
        descripcion: "Cumple con todos los requisitos fiscales.",
      }
    case 2:
      return {
        color: "warning",
        label: "Estado 2 - Riesgo Medio",
        icon: "mdi mdi-shield-alert",
        descripcion: "Cumplimiento parcial.",
      }
    case 3:
      return {
        color: "danger",
        label: "Estado 3 - Alto Riesgo",
        icon: "mdi mdi-shield-remove",
        descripcion: "Incumplimientos detectados.",
      }
    default:
      return {
        color: "secondary",
        label: "Sin información",
        icon: "mdi mdi-shield-off-outline",
        descripcion: "No se ha consultado el SISA",
      }
  }
}

// Alícuotas según RG 4310/2018
const getAlicuotas = (estado, categoria) => {
  // IVA según estado y categoría
  let iva, ganancias
  switch (estado) {
    case 0:
      iva = "10.5%"
      ganancias = "28%"
      break
    case 1:
      iva = "5%"
      ganancias = "2%"
      break
    case 2:
      // Estado 2: 7% productor, 5% operador
      iva = categoria === "PRODUCTOR" ? "7%" : "5%"
      ganancias = "2%"
      break
    case 3:
      iva = "8%"
      ganancias = "15%"
      break
    default:
      iva = "-"
      ganancias = "-"
  }
  return { iva, ganancias }
}

// Categorías SISA
const categoriasSisa = [
  { value: "PRODUCTOR", label: "Productor de granos" },
  { value: "OPERADOR_CON_PLANTA", label: "Operador con planta" },
  { value: "OPERADOR_SIN_PLANTA", label: "Operador sin planta" },
  { value: "CORREDOR", label: "Corredor" },
  { value: "ACOPIADOR", label: "Acopiador" },
  { value: "EXPORTADOR", label: "Exportador" },
  { value: "PROPIETARIO", label: "Propietario de tierras" },
  { value: "OTRO", label: "Otro" },
]

const DatosSisa = ({
  cuit,
  datosSisa,
  onSave,
  onConsultarAfip,
  readOnly = false,
}) => {
  const { puedeModificarObjeto } = useAuthorization()
  const [loading, setLoading] = useState(false)
  const [consultando, setConsultando] = useState(false)
  const [modoManual, setModoManual] = useState(false)
  const [modalAyuda, setModalAyuda] = useState(false)
  const [errorConsulta, setErrorConsulta] = useState(null)
  const [archivoConstancia, setArchivoConstancia] = useState(null)

  const setSuccessToast = toastHandler(state => state.setSuccess)
  const setErrorToast = toastHandler(state => state.setError)
  const setWarningsToast = toastHandler(state => state.setWarnings)

  // Consultar SISA en AFIP automáticamente
  const handleConsultarAfip = async () => {
    try {
      setConsultando(true)
      setErrorConsulta(null)

      const response = await onConsultarAfip()

      if (response && response.estadoSisa) {
        setSuccessToast(
          `SISA consultado: Estado ${response.estadoSisa} - ${getEstadoConfig(response.estadoSisa).label}`,
        )
      } else if (response && response.mensaje) {
        setErrorConsulta(response.mensaje)
        setWarningsToast(response.mensaje)
        setModoManual(true)
      } else {
        setErrorConsulta("CUIT no encontrado en SISA")
        setModoManual(true)
      }
    } catch (error) {
      console.error("Error consultando SISA:", error)
      const mensaje =
        error.response?.data?.mensaje ||
        error.response?.data?.error ||
        error.response?.data ||
        "Error al consultar AFIP"
      setErrorConsulta(mensaje)
      setErrorToast(mensaje)
      setModoManual(true)
    } finally {
      setConsultando(false)
    }
  }

  // Guardar datos manuales
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true)
      await onSave(values, archivoConstancia)
      setSuccessToast("Datos SISA guardados correctamente")
      setModoManual(false)
    } catch (error) {
      console.error("Error guardando SISA:", error)
      setErrorToast("Error al guardar los datos SISA")
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  const estadoConfig = getEstadoConfig(datosSisa?.estadoSisa)
  const alicuotas = getAlicuotas(
    datosSisa?.estadoSisa,
    datosSisa?.categoriaSisa,
  )

  // Valores iniciales para formulario manual
  const initialValues = {
    estadoSisa: datosSisa?.estadoSisa || null,
    categoriaSisa: datosSisa?.categoriaSisa || "PRODUCTOR",
    activoSisa: datosSisa?.activoSisa ?? true,
    observaciones: datosSisa?.observaciones || "",
  }

  return (
    <>
      <Card className="mb-4">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="mdi mdi-clipboard-check-outline me-2"></i>
              SISA - Sistema de Información Simplificado Agrícola
            </h5>
            <small className="text-muted">
              Consulta pública AFIP - Estado y alícuotas de retención
            </small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button
              color="link"
              size="sm"
              onClick={() => setModalAyuda(true)}
              id="btn-ayuda-sisa"
            >
              <i className="mdi mdi-help-circle-outline fs-5"></i>
            </Button>
            <UncontrolledTooltip
              target="btn-ayuda-sisa"
              transition={{ timeout: 150 }}
            >
              ¿Qué es el SISA?
            </UncontrolledTooltip>

            {datosSisa?.estadoSisa != null && (
              <Badge color={estadoConfig.color} className="fs-6 px-3 py-2">
                <i className={`${estadoConfig.icon} me-1`}></i>
                {estadoConfig.label}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardBody>
          {/* =================================================== */}
          {/* VISTA 1: Sin datos - Mostrar botón de consulta */}
          {/* =================================================== */}
          {datosSisa?.estadoSisa == null && !modoManual && (
            <div className="text-center py-4">
              <div className="mb-4">
                <i
                  className="mdi mdi-database-search text-primary"
                  style={{ fontSize: "64px" }}
                ></i>
              </div>
              <h5>Consultar SISA en AFIP</h5>
              <p className="text-muted mb-2">
                CUIT: <strong>{cuit}</strong>
              </p>
              <p className="text-muted mb-4">
                Consultaremos el estado SISA del productor directamente desde la
                base de datos pública de AFIP.
              </p>

              {errorConsulta && (
                <Alert color="warning" className="mb-4 text-start">
                  <i className="mdi mdi-alert-outline me-2"></i>
                  {errorConsulta}
                </Alert>
              )}

              {puedeModificarObjeto("/agro/establecimientos") && (
                <div className="d-flex justify-content-center gap-3">
                  <Button
                    color="primary"
                    size="lg"
                    onClick={handleConsultarAfip}
                    disabled={consultando}
                  >
                    {consultando ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Consultando AFIP...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-cloud-search me-2"></i>
                        Consultar SISA
                      </>
                    )}
                  </Button>

                  <Button
                    color="outline-secondary"
                    onClick={() => setModoManual(true)}
                  >
                    <i className="mdi mdi-pencil me-2"></i>
                    Cargar manualmente
                  </Button>
                </div>
              )}

              {consultando && (
                <div className="mt-4">
                  <Progress
                    animated
                    color="primary"
                    value={100}
                    className="mb-2"
                  />
                  <small className="text-muted">
                    Conectando con servicioscf.afip.gob.ar...
                  </small>
                </div>
              )}
            </div>
          )}

          {/* =================================================== */}
          {/* VISTA 2: Con datos - Mostrar resultados */}
          {/* =================================================== */}
          {datosSisa?.estadoSisa != null && !modoManual && (
            <>
              <Row>
                {/* Card Estado */}
                <Col md={4}>
                  <Card
                    className={`border-${estadoConfig.color} border-2 h-100`}
                  >
                    <CardBody className="text-center d-flex flex-column justify-content-center">
                      <i
                        className={`${estadoConfig.icon} text-${estadoConfig.color}`}
                        style={{ fontSize: "48px" }}
                      ></i>
                      <h4 className={`mt-3 text-${estadoConfig.color}`}>
                        Estado {datosSisa.estadoSisa}
                      </h4>
                      <p className="text-muted mb-0">
                        {estadoConfig.descripcion}
                      </p>
                    </CardBody>
                  </Card>
                </Col>

                {/* Card Retención IVA */}
                <Col md={4}>
                  <Card className="h-100 text-muted">
                    <CardBody className="text-center d-flex flex-column justify-content-center">
                      <h6 className="text-muted mb-2">Retención IVA</h6>
                      <h2 className="text-primary mb-1">{alicuotas.iva}</h2>
                      <small className="text-muted">Según RG 4310/2018</small>
                    </CardBody>
                  </Card>
                </Col>

                {/* Card Retención Ganancias */}
                <Col md={4}>
                  <Card className="h-100">
                    <CardBody className="text-center d-flex flex-column justify-content-center">
                      <h6 className="text-muted mb-2">Retención Ganancias</h6>
                      <h2 className="text-primary mb-1">
                        {alicuotas.ganancias}
                      </h2>
                      <small className="text-muted">Según RG 4310/2018</small>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Detalles adicionales */}
              <Row className="mt-4">
                <Col md={6}>
                  <table className="table table-sm mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted" style={{ width: "40%" }}>
                          Categoría
                        </td>
                        <td>
                          <strong>
                            {datosSisa.categoriaSisaDescripcion ||
                              categoriasSisa.find(
                                c => c.value === datosSisa.categoriaSisa,
                              )?.label ||
                              datosSisa.categoriaSisa ||
                              "Productor"}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Situación</td>
                        <td>
                          <Badge
                            color={
                              datosSisa.activoSisa !== false
                                ? "success"
                                : "secondary"
                            }
                          >
                            {datosSisa.activoSisa !== false
                              ? "Activo"
                              : "Inactivo"}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Fecha consulta</td>
                        <td>
                          {datosSisa.fechaVerificacion
                            ? new Date(
                                datosSisa.fechaVerificacion,
                              ).toLocaleDateString("es-AR")
                            : new Date().toLocaleDateString("es-AR")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3 bg-light h-100">
                    <h6 className="mb-2">
                      <i className="mdi mdi-chart-line me-2"></i>
                      Impacto en Scoring
                    </h6>
                    <span className={`fs-3 fw-bold text-${estadoConfig.color}`}>
                      {datosSisa.estadoSisa === 0
                        ? "-20%"
                        : datosSisa.estadoSisa === 1
                          ? "+10%"
                          : datosSisa.estadoSisa === 2
                            ? "0%"
                            : "-15%"}
                    </span>
                    <small className="text-muted d-block mt-1">
                      Ajuste al score crediticio agropecuario
                    </small>
                  </div>
                </Col>
              </Row>

              {/* Botones de acción */}
              <div className="text-end mt-4 pt-3 border-top">
                {puedeModificarObjeto("/agro/establecimientos") && (
                  <Button
                    color="outline-primary"
                    onClick={handleConsultarAfip}
                    disabled={consultando}
                    className="me-2"
                  >
                    {consultando ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Actualizando...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-refresh me-1"></i> Actualizar
                        desde ARCA
                      </>
                    )}
                  </Button>
                )}
                {puedeModificarObjeto("/agro/establecimientos") && (
                  <Button
                    color="outline-secondary"
                    onClick={() => setModoManual(true)}
                  >
                    <i className="mdi mdi-pencil me-1"></i>
                    Editar manualmente
                  </Button>
                )}
              </div>
            </>
          )}

          {/* =================================================== */}
          {/* VISTA 3: Formulario manual */}
          {/* =================================================== */}
          {modoManual && (
            <>
              <Alert color="info" className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <i className="mdi mdi-information-outline me-2"></i>
                    <strong>Carga manual</strong> - Complete los datos según la
                    constancia SISA del productor.
                  </div>
                  <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => {
                      setModoManual(false)
                      setErrorConsulta(null)
                    }}
                  >
                    <i className="mdi mdi-arrow-left me-1"></i>
                    Volver
                  </Button>
                </div>
              </Alert>

              <Formik
                initialValues={initialValues}
                validationSchema={sisaValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <Form onSubmit={handleSubmit} className="text-muted">
                    <Row>
                      {/* Estado SISA */}
                      <Col md={4}>
                        <FormGroup>
                          <Label for="estadoSisa">
                            Estado SISA <span className="text-danger">*</span>
                          </Label>
                          <Field
                            as={Input}
                            type="select"
                            name="estadoSisa"
                            id="estadoSisa"
                            disabled={readOnly}
                            invalid={errors.estadoSisa && touched.estadoSisa}
                            onChange={e =>
                              setFieldValue(
                                "estadoSisa",
                                e.target.value
                                  ? parseInt(e.target.value)
                                  : null,
                              )
                            }
                          >
                            <option value="">Seleccionar...</option>
                            <option value="1">Estado 1 - Bajo Riesgo</option>
                            <option value="2">Estado 2 - Riesgo Medio</option>
                            <option value="3">Estado 3 - Alto Riesgo</option>
                          </Field>
                          <ErrorMessage
                            name="estadoSisa"
                            component="div"
                            className="invalid-feedback d-block"
                          />
                          {values.estadoSisa && (
                            <small className="text-muted d-block mt-1">
                              {getEstadoConfig(values.estadoSisa).descripcion}
                            </small>
                          )}
                        </FormGroup>
                      </Col>

                      {/* Categoría */}
                      <Col md={4}>
                        <FormGroup>
                          <Label for="categoriaSisa">Categoría</Label>
                          <Field
                            as={Input}
                            type="select"
                            name="categoriaSisa"
                            id="categoriaSisa"
                            disabled={readOnly}
                          >
                            {categoriasSisa.map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.label}
                              </option>
                            ))}
                          </Field>
                        </FormGroup>
                      </Col>

                      {/* Activo */}
                      <Col md={4}>
                        <FormGroup>
                          <Label>Situación en SISA</Label>
                          <div className="form-check form-switch mt-2">
                            <Field
                              type="checkbox"
                              name="activoSisa"
                              id="activoSisa"
                              className="form-check-input"
                              disabled={readOnly}
                            />
                            <Label
                              className="form-check-label"
                              for="activoSisa"
                            >
                              {values.activoSisa ? (
                                <Badge color="success">Activo</Badge>
                              ) : (
                                <Badge color="secondary">Inactivo</Badge>
                              )}
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="mt-3">
                      {/* Constancia PDF */}
                      <Col md={6}>
                        <FormGroup>
                          <Label>Constancia SISA (PDF)</Label>
                          <Input
                            type="file"
                            accept=".pdf"
                            disabled={readOnly}
                            onChange={e =>
                              setArchivoConstancia(e.target.files[0])
                            }
                          />
                          <small className="text-muted">
                            Opcional: adjuntar constancia del productor
                          </small>
                        </FormGroup>
                      </Col>

                      {/* Observaciones */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="observaciones">Observaciones</Label>
                          <Field
                            as={Input}
                            type="textarea"
                            name="observaciones"
                            id="observaciones"
                            rows={2}
                            disabled={readOnly}
                            placeholder="Notas adicionales..."
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Preview de alícuotas */}
                    {values.estadoSisa && (
                      <div className="bg-light rounded p-3 mt-3">
                        <Row>
                          <Col md={4}>
                            <small className="text-muted d-block">
                              Retención IVA
                            </small>
                            <strong>
                              {
                                getAlicuotas(
                                  values.estadoSisa,
                                  values.categoriaSisa,
                                ).iva
                              }
                            </strong>
                          </Col>
                          <Col md={4}>
                            <small className="text-muted d-block">
                              Retención Ganancias
                            </small>
                            <strong>
                              {
                                getAlicuotas(
                                  values.estadoSisa,
                                  values.categoriaSisa,
                                ).ganancias
                              }
                            </strong>
                          </Col>
                          <Col md={4}>
                            <small className="text-muted d-block">
                              Factor Scoring
                            </small>
                            <strong
                              className={`text-${getEstadoConfig(values.estadoSisa).color}`}
                            >
                              {values.estadoSisa === 1
                                ? "+10%"
                                : values.estadoSisa === 2
                                  ? "0%"
                                  : "-15%"}
                            </strong>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Botones */}
                    {!readOnly && (
                      <div className="text-end mt-4">
                        <Button
                          color="secondary"
                          className="me-2"
                          onClick={() => {
                            setModoManual(false)
                            setErrorConsulta(null)
                          }}
                          disabled={isSubmitting}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          color="primary"
                          disabled={isSubmitting || loading}
                        >
                          {isSubmitting || loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-content-save me-2"></i>
                              Guardar
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal de Ayuda */}
      <Modal isOpen={modalAyuda} toggle={() => setModalAyuda(false)} size="lg">
        <ModalHeader toggle={() => setModalAyuda(false)}>
          <i className="mdi mdi-information-outline me-2"></i>
          ¿Qué es el SISA?
        </ModalHeader>
        <ModalBody>
          <p>
            El{" "}
            <strong>Sistema de Información Simplificado Agrícola (SISA)</strong>{" "}
            es un registro de AFIP que unifica varios padrones agrícolas y
            asigna un estado de riesgo fiscal.
          </p>

          <h6 className="mt-4">
            Estados y Alícuotas de Retención (RG 4310/2018)
          </h6>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Estado</th>
                  <th>Riesgo</th>
                  <th>IVA (Productor)</th>
                  <th>IVA (Operador)</th>
                  <th>Ganancias</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Badge color="success">1</Badge>
                  </td>
                  <td>Bajo</td>
                  <td>5%</td>
                  <td>5%</td>
                  <td>2%</td>
                </tr>
                <tr>
                  <td>
                    <Badge color="warning">2</Badge>
                  </td>
                  <td>Medio</td>
                  <td>7%</td>
                  <td>5%</td>
                  <td>2%</td>
                </tr>
                <tr>
                  <td>
                    <Badge color="danger">3</Badge>
                  </td>
                  <td>Alto</td>
                  <td>8%</td>
                  <td>8%</td>
                  <td>15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Alert color="success" className="mt-4">
            <i className="mdi mdi-check-circle me-2"></i>
            <strong>Consulta automática:</strong> Este sistema consulta
            directamente la base de datos pública de AFIP para obtener el estado
            actualizado.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalAyuda(false)}>
            Cerrar
          </Button>
          <Button
            color="primary"
            tag="a"
            href="https://servicioscf.afip.gob.ar/registros/sisa/sisa.aspx"
            target="_blank"
          >
            <i className="mdi mdi-open-in-new me-1"></i>
            Consultar en AFIP
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default DatosSisa
